import { useEffect, useState } from 'react'
import CatImg from '@/assets/img/cat_img.png'
import appleImg from '@/assets/img/nol_list.png'
import ListenButton from '@/components/atoms/ListenButton/ListenButton'
import RecordingBox from '@/components/atoms/RecordingBox/RecordingBox'
import { getStudentActivityTitle } from '@/utils/activityTitle';

const toBoolean = (value: any): boolean => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (!normalized) return false
    if (['false', '0', 'n', 'no'].includes(normalized)) return false
    return ['y', 'yes', 'true', '1'].includes(normalized)
  }
  return Boolean(value)
}

const pickFirstNonEmpty = (...candidates: Array<string | null | undefined>) => {
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim()
      if (trimmed.length > 0) {
        return trimmed
      }
    }
  }
  return null
}

interface FlashCardResponse {
  recordingBlob?: Blob;
  recordingUrl?: string;
  isCorrect?: boolean;
  listened?: boolean;
  isHomeworkReady?: boolean;
}

interface FlashCardProps {
  activity?: any;
  unitData?: any;
  onResponse?: (response: FlashCardResponse) => void;
  existingResponse?: FlashCardResponse;
  isHomework?: boolean;
}

export default function StudentFlashCard({ activity, unitData, onResponse, existingResponse }: FlashCardProps) {
  const [restoredRecordingUrl, setRestoredRecordingUrl] = useState<string | null>(null)
  const [hasListened, setHasListened] = useState(false)
  const [hasRecorded, setHasRecorded] = useState(Boolean(existingResponse?.recordingBlob || existingResponse?.recordingUrl));
  const [imageLoadError, setImageLoadError] = useState(false); // 이미지 로드 에러 상태

  // 진행 상태 복원/초기화 로직은 아래에서 recordingEnabled, audioUrl 계산 후 처리

  useEffect(() => {
    let temporaryObjectUrl: string | null = null;

    if (existingResponse?.recordingUrl) {
      if (existingResponse.recordingUrl.startsWith('blob:') && existingResponse.recordingBlob instanceof Blob) {
        temporaryObjectUrl = URL.createObjectURL(existingResponse.recordingBlob);
        setRestoredRecordingUrl(temporaryObjectUrl);
      } else {
        setRestoredRecordingUrl(existingResponse.recordingUrl);
      }
      return () => {
        if (temporaryObjectUrl) {
          URL.revokeObjectURL(temporaryObjectUrl);
      }
      };
    }

    if (existingResponse?.recordingBlob instanceof Blob) {
      temporaryObjectUrl = URL.createObjectURL(existingResponse.recordingBlob);
      setRestoredRecordingUrl(temporaryObjectUrl);
      return () => {
        if (temporaryObjectUrl) {
          URL.revokeObjectURL(temporaryObjectUrl);
        }
      };
    }

    setRestoredRecordingUrl(null);
    return () => {
      if (temporaryObjectUrl) {
        URL.revokeObjectURL(temporaryObjectUrl);
    }
    };
  }, [existingResponse]);
  
  const handleAudioEnded = () => {
    if (!recordingEnabled) {
      setHasListened(true);
    }
  };
  
  console.log('=== FlashCard 기존 응답 확인 ===');
  console.log('Existing Response:', existingResponse);
  
  // configJson에서 recordingEnabled 확인
  let config: any = {};
  try {
    if (activity?.configJson) {
      config = typeof activity.configJson === 'string' 
        ? JSON.parse(activity.configJson) 
        : activity.configJson;
    }
  } catch (e) {
    console.error('configJson 파싱 에러:', e);
  }
  
  const recordingEnabled = toBoolean(config.recordingEnabled);
  
  console.log('=== FlashCard Config ===');
  console.log('Config:', config);
  console.log('Recording Enabled:', recordingEnabled);
  
  // activity.items에서 실제 데이터 가져오기
  const activityData = activity?.items?.[0] || {};
  let content: any = {};
  
  console.log('=== FlashCard 원본 데이터 ===');
  console.log('Activity:', activity);
  console.log('Activity Items:', activity?.items);
  console.log('First Item:', activityData);
  console.log('ContentJson (raw):', activityData.contentJson);
  
  try {
    if (activityData.contentJson) {
      content = typeof activityData.contentJson === 'string' 
        ? JSON.parse(activityData.contentJson) 
        : activityData.contentJson;
    }
  } catch (e) {
    console.error('contentJson 파싱 에러:', e);
    console.error('파싱 시도한 값:', activityData.contentJson);
  }

  console.log('Parsed Content:', content);
  console.log('Content keys:', Object.keys(content));

  const word = content.word || content.text || content.english || 'Cat';
  const hasImage = !!(content.wordImageUrl || content.imageUrl || content.image || content.imagePath || content.img);
  const imageUrl = hasImage ? (content.wordImageUrl || content.imageUrl || content.image || content.imagePath || content.img) : appleImg;
  const audioUrl = pickFirstNonEmpty(
    content.audioUrl,
    content.wordAudioUrl,
    content.audio,
    content.wordSoundUrl,
    content.soundUrl,
    content.audioPath,
    content.sound,
    content.voiceUrl,
  ); // [QA Fix][Row 104] 다양한 음원 필드를 허용
  const audioAvailable = Boolean(audioUrl);
  const title = getStudentActivityTitle(
    activity?.title,
    activity?.instructions || '2.단어를 직접 녹음하고 들어보세요.'
  );

  const computeHomeworkReady = (listenedFlag?: boolean, recordedFlag?: boolean) => {
    if (recordingEnabled) {
      return recordedFlag ?? hasRecorded;
    }
    if (!audioAvailable) {
      return true;
    }
    return listenedFlag ?? hasListened;
  };

  // 녹음 완료 핸들러
  const handleRecordingComplete = (audioBlob: Blob, audioUrl: string) => {
    console.log('녹음 완료:', audioBlob, audioUrl);
    
    // 부모 컴포넌트에 즉시 전달 (다음 버튼 클릭 시 사용하도록)
    setHasRecorded(true);
    if (onResponse) {
      onResponse({ 
        recordingBlob: audioBlob, 
        recordingUrl: audioUrl,
        isCorrect: true, // 녹음 완료 = 정답
        isHomeworkReady: computeHomeworkReady(undefined, true)
      });
    }
  };

  useEffect(() => {
    if (!onResponse) {
      return;
    }

    if (existingResponse) {
      if (recordingEnabled) {
        const hasRecording = Boolean(existingResponse.recordingBlob || existingResponse.recordingUrl);
        setHasListened(false);
        if (hasRecording && existingResponse.isCorrect !== true) {
          onResponse({
            ...existingResponse,
            isCorrect: true,
            isHomeworkReady: computeHomeworkReady(undefined, true),
          });
        }
      } else {
        const listenedBefore = !audioAvailable || Boolean(existingResponse.listened || existingResponse.isCorrect);
        setHasListened(listenedBefore);
        if (listenedBefore && (existingResponse.isCorrect !== true || existingResponse.listened !== true)) {
          onResponse({
            ...existingResponse,
            listened: true,
            isCorrect: true,
            isHomeworkReady: computeHomeworkReady(listenedBefore, undefined),
          });
        }
      }
      return;
    }

    if (recordingEnabled) {
      onResponse({
        recordingBlob: undefined,
        recordingUrl: undefined,
        isCorrect: false,
        isHomeworkReady: false,
      });
      setHasListened(false);
    } else {
      if (!audioAvailable) {
        setHasListened(true);
        onResponse({
          listened: true,
          isCorrect: true,
          isHomeworkReady: true,
        });
      } else {
        setHasListened(false);
        onResponse({
          listened: false,
          isCorrect: false,
          isHomeworkReady: false,
        });
      }
    }
  }, [audioAvailable, existingResponse, onResponse, recordingEnabled]);

  useEffect(() => {
    if (recordingEnabled || !onResponse) {
      return;
    }
    if (hasListened) {
      if (!existingResponse || existingResponse.listened !== true || existingResponse.isCorrect !== true) {
        onResponse({
          ...(existingResponse || {}),
          listened: true,
          isCorrect: true,
          isHomeworkReady: computeHomeworkReady(true, undefined),
        });
      }
    }
  }, [existingResponse, hasListened, onResponse, recordingEnabled]);

  console.log('=== FlashCard 추출 데이터 ===');
  console.log('Word:', word);
  console.log('Image URL:', imageUrl);
  console.log('Audio URL:', audioUrl);

  return (
    <main>
     <div>
        <h1 className='px-5 pt-5 pb-3 heading1'>{title}</h1>
        <div className='p-5 pt-0'>
            <div className='flex overflow-hidden relative flex-col gap-2 justify-center items-center p-4 w-full rounded-2xl border border-gray-200'>
                <p className='text-5xl font-bold text-center'>{word}</p>
                <div className='relative size-[200px] border border-gray-100 flex items-center justify-center rounded-2xl overflow-hidden bg-gray-50'>
                    {hasImage && !imageLoadError ? (
                        <img 
                            src={imageUrl} 
                            alt={word} 
                            className='max-w-[200px] object-cover w-full h-full'
                            onError={() => {
                                setImageLoadError(true);
                            }}
                        />
                    ) : (
                        <div className="flex flex-col justify-center items-center text-sm text-gray-400 size-full">
                            <img src={appleImg} alt="no list" className="size-20" />
                            <p className="text-sm text-gray-400">이미지가 없습니다.</p>
                        </div>
                    )}
                </div>
                   <ListenButton size={48} className='absolute top-4 right-4' audioSrc={audioUrl} onAudioEnded={handleAudioEnded} />
               </div>
               
               {/* 녹음 UI (recordingEnabled가 true일 때만 표시) */}
               {recordingEnabled && (
                 <RecordingBox 
                   onRecordingComplete={handleRecordingComplete}
                  existingAudioUrl={restoredRecordingUrl}
                 />
               )}
           </div>
        </div>
       </main>
     )
   }